import getConfig from 'next/config';
// eslint-disable-next-line import/no-cycle
import { getAddressList, getPrequalResult, getAreaCodes } from '../../integration/prequal.api';
import {
  getTariffs,
  getRecommendedBBTariffs,
  removeTariffs,
  addBroadbandTariffs,
  addTariffs,
} from '../../integration/tariff.api';
import { onload as tvOnload } from '../eir-tv/Tv.util';
import structureData from '../../../util/Format.util';
import getCarts from '../../integration/cart.api';
import { prequalPreOrderAnalytics, viewItemListAnalytics } from '../../../util/gtm';
import useLocalStorage, { getLocalStorage, setCode } from '../../hooks/useLocalStorage';
import { getCookie, getTokenFromCookies } from '../../hooks/useCookie';
import { triggerNEQ } from './NBI.util';
import { SERVICE_GROUPS } from '../shared/Constants';

const { publicRuntimeConfig } = getConfig();
const localStore = useLocalStorage();

const EIRCODE_PATTERN =
  /\b(?:(a(4[125s]|6[37]|7[5s]|[8b][1-6s]|9[12468b])|c1[5s]|d([0o][1-9sb]|1[0-8osb]|2[024o]|6w)|e(2[15s]|3[24]|4[15s]|[5s]3|91)|f(12|2[368b]|3[15s]|4[25s]|[5s][26]|9[1-4])|h(1[2468b]|23|[5s][34]|6[25s]|[79]1)|k(3[246]|4[5s]|[5s]6|67|7[8b])|n(3[79]|[49]1)|p(1[247]|2[45s]|3[126]|4[37]|[5s][16]|6[17]|7[25s]|[8b][15s])|r(14|21|3[25s]|4[25s]|[5s][16]|9[35s])|t(12|23|34|4[5s]|[5s]6)|v(1[45s]|23|3[15s]|42|9[2-5s])|w(12|23|34|91)|x(3[5s]|42|91)|y(14|2[15s]|3[45s]))\s?[abcdefhknoprtsvwxy\d]{4})\b/i;

/**
 * @name getAddresses - Function for getting address list based on given eircode
 * @param {*} graphql - From useApolloClient
 * @param {string} eirCode - Eircode provided by user on prequal form
 * @returns {string} - Array of addresses related to eircode
 */
const getAddresses = async (graphql, eirCode) => {
  try {
    if (eirCode === '') return [];
    const data = await getAddressList(graphql, eirCode);
    return data.addressList.addresses;
  } catch (error) {
    throw new Error(error.message);
  }
};

const getAreaCodesList = async graphql => {
  try {
    const res = await getAreaCodes(graphql);
    return res.data.areaCodes;
  } catch (error) {
    throw new Error(error.message);
  }
};
/**
 * @name validateEircode - function for Eircode validation
 * @param {string} eircode - Eircode entered on prequal form
 * @returns {string} - If eircode is valid, eircode is returned otherwise empty string
 */
const validateEircode = eircode => {
  const pattern = EIRCODE_PATTERN;

  const reg = new RegExp(pattern, 'i');
  const i = String(eircode).search(reg);
  let validEircode = '';
  if (i !== -1) {
    validEircode = String(eircode)
      .substring(i, i + 8)
      .toUpperCase()
      .replace(' ', '')
      .replace(/O/g, 0)
      .replace(/S/g, 5)
      .replace(/B/g, 8);
    return validEircode;
  }
  return validEircode;
};
/**
 * @name formatAddress - Method for formatting the address object recieved by AddressList API, into a string for showing into the dropdown
 * @param {object} option - It is the address Object return by AddressList API
 * @returns {string} - Formatted address for dropdown
 */
function formatAddress(option, servicesForSubscription) {
  let addressLine1 = '';
  let addressLine2 = '';
  let addressLine3 = '';
  let town = '';
  let county = '';
  let VOBNumber = '';
  if (option?.addressLine1 && option?.addressLine1 !== 'null') {
    addressLine1 = option.addressLine1
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }

  if (option?.addressLine2 && option?.addressLine2 !== 'null') {
    addressLine2 = option.addressLine2
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
    addressLine2 = `, ${addressLine2}`;
  }
  if (option?.addressLine3 && option?.addressLine3 !== 'null') {
    addressLine3 = option.addressLine3
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
    addressLine3 = `, ${addressLine3}`;
  }
  if (option?.town && option?.town !== 'null') {
    town = option.town
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
    town = `, ${town}`;
  }
  if (option?.county && option?.county !== 'null') {
    county = option.county;
    county = county.slice(3);
    county = county
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
    county = `, ${county}`;
  }

  let serviceWithSipNumber = servicesForSubscription?.filter(service => service.sipNumber !== null);

  if (serviceWithSipNumber?.length > 0) {
    serviceWithSipNumber = Object.assign({}, ...serviceWithSipNumber);
    VOBNumber = `, ${serviceWithSipNumber?.sipNumber}`;
  } else if (serviceWithSipNumber?.length === 0) {
    let serviceWithDsl = servicesForSubscription?.filter(service => service.dsl !== null);
    if (serviceWithDsl?.length > 0) {
      serviceWithDsl = Object.assign({}, ...serviceWithDsl);
      VOBNumber = `, ${serviceWithDsl?.dsl}`;
    } else {
      VOBNumber = '';
    }
  }

  return addressLine1 + addressLine2 + addressLine3 + town + county + VOBNumber;
}
/**
 * @name createAddressObject - Gives the address object when selected from dropdown addressList
 * @param {string} address - Address selected from dropdown
 * @returns {object} - Formatted address object
 */
const createAddressObject = (address, eirCode) => {
  let obj;
  if (!address) return {};
  obj = {
    eircode: eirCode || address.eircode,
  };

  obj = address.county
    ? { ...obj, ...{ county: address.county.replace('CO_', '') } }
    : { ...obj, ...{ county: '' } };

  obj = address.town ? { ...obj, ...{ town: address.town } } : { ...obj, ...{ town: '' } };

  obj = address.addressLine1
    ? { ...obj, ...{ addressLine1: address.addressLine1 } }
    : { ...obj, ...{ addressLine1: '' } };

  obj = address.addressLine2
    ? { ...obj, ...{ addressLine2: address.addressLine2 } }
    : { ...obj, ...{ addressLine2: '' } };

  obj = address.addressLine3
    ? { ...obj, ...{ addressLine3: address.addressLine3 } }
    : { ...obj, ...{ addressLine3: '' } };

  return obj;
};

const getStaticBroadband = async ({ graphql }) => {
  try {
    const offerData = await getTariffs({
      graphql,
      variables: {
        serviceGroupCode: SERVICE_GROUPS.DUALPLAY,
        advertised: true,
        acquisitionType: 'NEW_ACQUISITION',
      },
      staticBB: true,
    });
    const result = offerData?.tariffs?.map(offer => structureData(offer, graphql));

    return result;
  } catch (err) {
    console.error(err);
    return null;
  }
};
const getRecommendedBroadband = async ({ graphql }) => {
  try {
    const offerData = await getRecommendedBBTariffs({
      graphql,
    });
    const result = offerData?.map(offer => structureData(offer, graphql));
    return result;
  } catch (err) {
    console.error(err);
    return null;
  }
};
const getCOTariffs = async ({ graphql }) => {
  const token = getTokenFromCookies();
  const reference = getCookie('reference');
  try {
    setCode('CO');
    const prequalId = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT') || '{}')?.prequalId;
    if (prequalId) {
      const offerData = await getTariffs({
        graphql,
        variables: {
          serviceGroupCode: SERVICE_GROUPS.DUALPLAY,
          acquisitionType: 'CHANGE_OFFER',
          reference,
          token,
          prequalId,
          cartType: 'CHANGE_OFFER',
        },
      });
      const result = offerData?.tariffs?.map(offer => structureData(offer, graphql));

      return result;
    }
    return [];
  } catch (err) {
    return null;
  }
};

/**
 * @name getPrequal - Function for getting prequal data
 * @param {*} graphql
 * @param {object} address - formatted address object
 * @returns {object} - Post Prequal Data
 */
const getPrequal = async (graphql, address, phoneNumber = undefined, acquisitionType = '') => {
  try {
    const { data, result } = await getPrequalResult(graphql, address, phoneNumber, acquisitionType);
    if (result) {
      return { res: null, result };
    }
    return { res: data.prequal, result: null };
  } catch (error) {
    throw new Error(error);
  }
};
/**
 * @name addTariff - Function for adding tariff
 * @param {*} graphql
 * @returns {} - Tariff adding status
 */
const addTariff = async (graphql, code, offerCode, cart, cartType = '') => {
  let prospectReference = '';
  let prequalId = '';
  if (localStore.getLocalStorage('PROSPECTUUID')) {
    prospectReference = localStore.getLocalStorage('PROSPECTUUID');
  }
  if (localStore.getLocalStorage('PREQUAL_RESULT')) {
    prequalId = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT')).prequalId;
  }

  let variables = {
    prospectUuid: prospectReference,
    catalogOfferCode: offerCode,
    catalogTariffPlanCode: code,
    prequalId: parseInt(prequalId, 10),
    offers: cart?.offers
      ?.filter(offer => offer.baseOffer?.serviceGroup === SERVICE_GROUPS.DUALPLAY)
      .map(offer => ({ id: offer.id })),
    serviceGroup: SERVICE_GROUPS.DUALPLAY,
  };
  // if a mobile offer is already in cart
  // if (cart?.offers.length > 0) {
  //   const offerId = cart?.offers?.filter(offer => offer.baseOffer?.serviceGroup === 'DUALPLAY')[0]
  //     ?.id;
  //   variables = {
  //     ...variables,
  //     offerId,
  //   };
  // }
  if (cartType === 'CHANGE_OFFER') {
    setCode('CO');
    if (localStore.getLocalStorage('PREQUALID')) {
      prequalId = JSON.parse(localStore.getLocalStorage('PREQUALID'));
    }
    const reference = getCookie('reference');
    variables = {
      reference,
      catalogOfferCode: offerCode,
      catalogTariffPlanCode: code,
      prequalId: parseInt(prequalId, 10),
      offers: cart?.offers
        ?.filter(offer => offer.baseOffer?.serviceGroup === SERVICE_GROUPS.DUALPLAY)
        .map(offer => ({ id: offer.id })),
      serviceGroup: SERVICE_GROUPS.DUALPLAY,
      subscriptionId: parseInt(getCookie('subscription_id'), 10),
    };
    if (cart?.offers.length > 0) {
      const offerId = cart?.offers?.filter(offer => offer.baseOffer?.serviceGroup === 'DUALPLAY')[0]
        ?.id;
      variables = {
        ...variables,
        offerId,
      };
    }
    try {
      const data = await addTariffs({
        graphql,
        variables,
      });
      return data.addTariff;
    } catch (error) {
      throw new Error(error);
    }
  }

  // If prospect is not available in localstorage
  // if (!localStore.hasValue('prospectUuid')) {
  //   variables = {
  //     ...variables,
  //     prospectUuid: '',
  //     offerType: 'POSTPAY',
  //   };
  // }
  try {
    const data = await addBroadbandTariffs({
      graphql,
      variables,
    });
    return data.addBroadbandTariff;
  } catch (error) {
    throw new Error(error);
  }
};

const getSelectedBroadbandOfferFromCart = async ({
  graphql,
  localStorage,
  changeOffer = false,
}) => {
  // get the selected offer value from prospect.
  if (changeOffer || getCookie('cart_Type') === 'CHANGE_OFFER') setCode('CO');
  let variables = {};
  if (changeOffer || getCookie('cart_Type') === 'CHANGE_OFFER') {
    variables = {
      reference: getCookie('reference') || localStorage.getLocalStorage('REF'),
      cartType: 'CHANGE_OFFER',
      token: getTokenFromCookies(),
    };
  } else {
    variables = {
      reference: localStorage.getLocalStorage('PROSPECTUUID'),
    };
  }
  if (getCookie('reference') || getLocalStorage('PROSPECTUUID') || getLocalStorage('REF')) {
    try {
      const { getCart: cartData } = await getCarts({
        graphql,
        variables,
      });
      if (cartData === null) {
        return {
          cartData: {},
          selectedOffer: [],
        };
      }
      return {
        cartData,
        selectedOffer: cartData.offers?.filter(
          offer => offer?.baseOffer?.serviceGroup === SERVICE_GROUPS.DUALPLAY
        ),
      };
    } catch (err) {
      // if (err.message.includes(localStorage.getLocalStorage('REF') || getCookie('reference'))) {
      //   return {
      //     cartData: {},
      //     selectedOffer: [],
      //   };
      // }
      throw new Error(err.message);
      // if (err.message.includes(localStorage.getLocalStorage('REF') || getCookie('reference'))) {
      // return {
      //   cartData: {},
      //   selectedOffer: [],
      // };
      // }
      // throw new Error(err.message);
    }
  }
};
// on Broadband page load
const onload = async ({ graphql, localStorage, dispatch, changeOffer = false }) => {
  let result = {};
  let selectedBroadbandOffer = {};
  try {
    // if prospect & prequal is already present
    if (
      localStorage.hasValue('PROSPECTUUID') &&
      localStorage.hasValue('PREQUAL_RESULT') &&
      changeOffer === false
    ) {
      // getting selected broadband offer from cart
      const { selectedOffer, cartData } = await getSelectedBroadbandOfferFromCart({
        graphql,
        localStorage,
      });
      // get recommeneded offers
      const offerData = await getRecommendedBroadband({
        graphql,
      });
      viewItemListAnalytics('Broadband_Recommended_Tariffs', offerData);
      dispatch({ type: 'ENABLE_FOOTER', payload: false });
      result = {
        offers: offerData,
        cartData,
      };
      // if broadband tariff available in prospect
      if (selectedOffer?.length > 0) {
        // formatting select offer
        [selectedBroadbandOffer] = offerData.filter(
          offer => selectedOffer[0].baseOffer?.catalogTariffPlanCode === offer.code
        );
        let selectedTvOffer;
        if (selectedBroadbandOffer.isTVOfferIncluded)
          selectedTvOffer = await tvOnload({ localStorage: localStore, graphql });
        result = {
          ...result,
          selectedBroadbandOffer,
          cartData,
          selectedTvOffer,
        };
      }
    }
    // when prospect & prequal is not available.
    else if (changeOffer) {
      dispatch({ type: 'ENABLE_FOOTER', payload: false });
      // getting selected broadband offer from cart
      const { selectedOffer = {}, cartData } = await getSelectedBroadbandOfferFromCart({
        graphql,
        localStorage,
        changeOffer,
      });
      // get CO tariffs
      const offerData = await getCOTariffs({
        graphql,
      });
      viewItemListAnalytics('Broadband_CO_Tariffs', offerData);
      result = {
        offers: offerData,
        cartData,
      };
      // if broadband tariff available in prospect
      if (selectedOffer?.length > 0) {
        // formatting select offer
        [selectedBroadbandOffer] = offerData.filter(
          offer => selectedOffer[0].baseOffer?.catalogTariffPlanCode === offer.code
        );
        let selectedTvOffer;
        if (selectedBroadbandOffer.isTVOfferIncluded)
          selectedTvOffer = await tvOnload({ localStorage: localStore, graphql });
        result = {
          ...result,
          selectedBroadbandOffer,
          cartData,
          selectedTvOffer,
        };
      }
    } else {
      // get static
      const offerData = await getStaticBroadband({
        graphql,
      });
      viewItemListAnalytics('Broadband_Tariffs', offerData);
      dispatch({ type: 'ENABLE_FOOTER', payload: true });
      result = {
        offers: offerData,
      };
    }
  } catch (error) {
    result = { error };
    console.error('something went wrong on load of /shop/broadband', error);
  }
  return result;
};

const removeTariff = async (graphql, offerid, cartType = '') => {
  let prospectReference = '';

  prospectReference = localStore.getLocalStorage('PROSPECTUUID') || getCookie('reference');

  if (cartType === 'CHANGE_OFFER') {
    setCode('CO');
    const reference = getCookie('reference');
    const token = getTokenFromCookies();
    const variables = {
      reference,
      offerId: offerid,
      serviceGroup: 'DUALPLAY',
      cartType: 'CHANGE_OFFER',
      token,
    };
    try {
      const data = await removeTariffs({
        graphql,
        variables,
      });
      return data.removeTariff;
    } catch (error) {
      throw new Error(error);
    }
  }
  try {
    const data = await removeTariffs({
      graphql,
      variables: {
        reference: prospectReference,
        offerId: offerid,
      },
    });
    return data.removeTariff;
  } catch (error) {
    throw new Error(error);
  }
};
const checkFutureAvailability = date => {
  const defaultDate = new Date('2099-12-31');
  const rcvdDate = new Date(date);
  const currentDate = new Date();
  if (date === null || rcvdDate.toUTCString() === defaultDate.toUTCString()) return false;
  if (currentDate < rcvdDate) return true;
};
const getEligibleServicesWithFutureAvailability = (services = []) => {
  let eligibleService = [];

  eligibleService = services.map(item => {
    const obj = {};
    obj[item.broadbandTechnology] = true;
    obj.futureAvailability = checkFutureAvailability(item.serviceDate);
    obj.speed = item.maxSpeed;
    obj.futureDate = item.serviceDate;
    return obj;
  });
  return eligibleService;
};

// const redirectToR6 = () => {
//   window.location.href = '/broadband';
//   // Router.push(`${publicRuntimeConfig.NEXT_PUBLIC_PATH}/broadband`);
// };
const startNBIJourney = (
  setIsNBIJourney,
  Graphql,
  setNEQDetails,
  setLoading,
  errorBBModal,
  errorModalOccurence,
  errorModalMaxOccurence,
  neqCounter,
  setNeqCounter,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal,
  setIsPrequaled,
  setShowLoader,
  setprequalCTALoaderText
) => {
  setLoading(true);
  setIsNBIJourney(true);
  triggerNEQ(
    Graphql,
    setNEQDetails,
    setLoading,
    errorBBModal,
    errorModalOccurence,
    errorModalMaxOccurence,
    neqCounter,
    setNeqCounter,
    dataRetrievalModal,
    setErrorModalOccurence,
    setOnLoadError,
    openAPIFailureModal,
    setIsPrequaled,
    setShowLoader,
    setprequalCTALoaderText
  );
};
const redirectToMBB = (setIsMBBJourney = () => {}) => {
  // window.location.href = `${publicRuntimeConfig.NEXT_PUBLIC_PATH}/mobile/broadband`;
  setIsMBBJourney(true);
};

const checkService = (
  Graphql,
  services,
  isCS,
  setMaxSpeed,
  setIsMBBJourney,
  setIsNBIJourney,
  setLoading,
  setCurrentService,
  setFTTHinFuture,
  setShowPrequalTopTag,
  setFutureDate,
  setFutureSpeed,
  setNEQDetails,
  errorBBModal,
  errorModalOccurence,
  errorModalMaxOccurence,
  neqCounter,
  setNeqCounter,
  dataRetrievalModal,
  setErrorModalOccurence,
  setOnLoadError,
  openAPIFailureModal,
  setIsPrequaled,
  setShowLoader,
  setprequalCTALoaderText,
  path
) => {
  if (!services) return;
  const eligibleService = getEligibleServicesWithFutureAvailability(services);

  const isServiceExist = service => eligibleService.find(item => item[service] === true) || null;
  let currentService = 'FTTH';
  while (currentService !== '') {
    const service = isServiceExist(currentService);
    switch (currentService) {
      case 'FTTH': {
        if (service && service.futureAvailability === false) {
          setCurrentService('FTTH');
          setMaxSpeed(service.speed);
          currentService = '';
          break;
        } else if (!isCS) {
          if (service && service?.futureAvailability) {
            // This block is for Preorder
            setCurrentService('FTTH');
            prequalPreOrderAnalytics('Openeir', path);
            setMaxSpeed(service.speed);
            currentService = '';
            break;
          }
          currentService = 'NBI';
          break;
        } else {
          if (service && service?.futureAvailability) {
            // This block is for Preorder
            setCurrentService('FTTH');
            prequalPreOrderAnalytics('Openeir', path);
            setMaxSpeed(service.speed);
            currentService = '';
            break;
          }
          currentService = 'FTTC';
          break;
        }
      }
      case 'NBI': {
        if (service) {
          currentService = '';
          setMaxSpeed(service.speed);
          setCurrentService('NBI');
          setLoading(true);
          startNBIJourney(
            setIsNBIJourney,
            Graphql,
            setNEQDetails,
            setLoading,
            errorBBModal,
            errorModalOccurence,
            errorModalMaxOccurence,
            neqCounter,
            setNeqCounter,
            dataRetrievalModal,
            setErrorModalOccurence,
            setOnLoadError,
            openAPIFailureModal,
            setIsPrequaled,
            setShowLoader,
            setprequalCTALoaderText
          );
        } else currentService = 'FTTC';
        break;
      }
      case 'FTTC': {
        if (service && service.futureAvailability === false) {
          setCurrentService('FTTC');
          setMaxSpeed(service.speed);
          currentService = '';
          break;
        } else if (!isCS) {
          if (service && service?.futureAvailability) {
            // This block is for Preorder
            setCurrentService('FTTC');
            prequalPreOrderAnalytics('Openeir', path);
            setMaxSpeed(service.speed);
            currentService = '';
            break;
          }
          currentService = 'MBB';
          break;
        } else {
          if (service && service?.futureAvailability) {
            // This block is for Preorder
            setCurrentService('FTTC');
            prequalPreOrderAnalytics('Openeir', path);
            setMaxSpeed(service.speed);
            currentService = '';
            break;
          }
          currentService = '';
          setCurrentService('MBB');
          redirectToMBB(setIsMBBJourney);
        }
        break;
      }
      case 'MBB': {
        if (service) {
          setCurrentService('MBB');
          currentService = '';
          redirectToMBB(setIsMBBJourney);
        } else currentService = 'NGB';
        break;
      }
      case 'NGB': {
        if (service) {
          setCurrentService('MBB');
          currentService = '';
          redirectToMBB(setIsMBBJourney);
        } else {
          setCurrentService('MBB');
          currentService = '';
          redirectToMBB(setIsMBBJourney);
        }
        break;
      }
      default:
        break;
    }
  }
};

const getFilteredOfferData = data => {
  let filteredData = {};
  const have100MbOffer =
    data.filter(
      offers => offers.code === '100MB_BB_12_ACQ_ONLINE' || offers.code === '100MB_BB_24_ACQ_ONLINE'
    ).length > 0;
  const have7MbOffer =
    data.filter(
      offers => offers.code === '7MB_BB_12_ACQ_ONLINE' || offers.code === '7MB_BB_24_ACQ_ONLINE'
    ).length > 0;
  const have100MbAnd7MbOffer = have100MbOffer && have7MbOffer;
  if (have100MbAnd7MbOffer) {
    filteredData = data.filter(
      offers => offers.code !== '7MB_BB_12_ACQ_ONLINE' && offers.code !== '7MB_BB_24_ACQ_ONLINE'
    );
  } else filteredData = data;
  return filteredData;
};

/**
 * Formats internet speed
 * @param {number} speed - Positive speed value in Mbps (e.g., 100, 1000, 1500, etc.)
 * @returns {string} Formatted speed string (e.g., '100Mb', '1Gb', '1.5Gb') or a default value for invalid inputs
 */
const formatSpeed = speed => {
  if (typeof speed !== 'number' || Number.isNaN(speed) || speed < 0) {
    return ''; // Default empty string for invalid speeds (no display for invalid data)
  }

  // Format the speed based on the value, showing Gb for speeds >= 1000Mbps
  return speed >= 1000
    ? `${(speed / 1000).toFixed(speed % 1000 === 0 ? 0 : 1)}Gb` // Handle 1Gb, 1.5Gb, etc.
    : `${speed}Mb`; // Display in Mbps for speeds less than 1000
};

const isNewCustomer = () => {
  if (localStore.getLocalStorage('PREQUAL_RESULT')) {
    const { customerType } = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT'));
    return customerType === 'NEW';
  }
  return false;
};

/**
 * @function getPrequalBackgroundUrl
 * @param {object} props - CMS data object
 * @param {Boolean} isMobile - Checks mobile responsiveness
 * @param {Boolean} isPrequaled - Checkes the prequal state
 * @returns {string} url of the image
 */
const getPrequalBackgroundUrl = (props, isMobile, isPrequaled) => {
  let url;
  if (!isPrequaled) {
    url = isMobile
      ? `${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${props?.beforePrequelMobileBkgImg?.data?.attributes?.url}`
      : `${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${props?.beforePrequelDesktopbkgImg?.data?.attributes?.url}`;
  } else {
    url = isMobile
      ? `${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${props?.afterPrequelMobilebkgImg?.data?.attributes?.url}`
      : `${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${props?.afterPrequelDesktopbkgImg?.data?.attributes?.url}`;
  }
  return url;
};

/**
 * @function getPrequalBackgroundAltText
 * @param {object} props - CMS data object
 * @param {Boolean} isMobile - Checks mobile responsiveness
 * @param {Boolean} isPrequaled - Checkes the prequal state
 * @returns {string} alternative text for image
 */
const getPrequalBackgroundAltText = (props, isMobile, isPrequaled) => {
  let altText;
  if (!isPrequaled) {
    altText = isMobile
      ? props?.beforePrequelMobileBkgImg?.data?.attributes?.alternativeText
      : props?.beforePrequelDesktopbkgImg?.data?.attributes?.alternativeText;
  } else {
    altText = isMobile
      ? props?.afterPrequelMobilebkgImg?.data?.attributes?.alternativeText
      : props?.afterPrequelDesktopbkgImg?.data?.attributes?.alternativeText;
  }
  return altText;
};

export {
  validateEircode,
  getAddresses,
  formatAddress,
  createAddressObject,
  getPrequal,
  getStaticBroadband,
  getRecommendedBroadband,
  addTariff,
  onload,
  removeTariff,
  getAreaCodesList,
  checkFutureAvailability,
  getEligibleServicesWithFutureAvailability,
  checkService,
  getFilteredOfferData,
  formatSpeed,
  isNewCustomer,
  getPrequalBackgroundAltText,
  getPrequalBackgroundUrl,
};
